import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  JellyfinItem,
  markItemPlayed,
  markItemUnplayed,
} from "../api/jellyfin";
import { Credentials } from "../storage/credentials";

const PLAYED_STATE_ITEM_TYPES = new Set(["Episode", "Movie", "Season", "Series"]);

export function supportsPlayedState(item: JellyfinItem) {
  return PLAYED_STATE_ITEM_TYPES.has(item.Type ?? "");
}

export function usePlayedStateToggle(
  item: JellyfinItem,
  credentials: Credentials,
) {
  const queryClient = useQueryClient();
  const [played, setPlayed] = useState(Boolean(item.UserData?.Played));
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setPlayed(Boolean(item.UserData?.Played));
  }, [item.Id, item.UserData?.Played]);

  const togglePlayed = useCallback(async () => {
    if (!item.Id || pending || !supportsPlayedState(item)) return;
    const next = !played;
    setPlayed(next);
    setPending(true);
    try {
      if (next) await markItemPlayed(credentials, item.Id);
      else await markItemUnplayed(credentials, item.Id);
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          const root = String(queryKey[0] ?? "");
          return root === "home"
            || root.startsWith("catalog")
            || root.startsWith("collection")
            || root === "person-filmography"
            || root.startsWith("item-")
            || (root === "item" && queryKey[1] === item.Id);
        },
      });
    } catch {
      setPlayed(!next);
    } finally {
      setPending(false);
    }
  }, [credentials, item, pending, played, queryClient]);

  return {
    played,
    pending,
    supported: supportsPlayedState(item),
    togglePlayed,
  };
}
